import React, { useEffect, useState } from "react";
import {
    AppLayout,
    ContentLayout,
    Flashbar,
    Header,
    Link,
    SideNavigation,
    SideNavigationProps,
} from "@cloudscape-design/components";
import { I18nProvider } from "@cloudscape-design/components/i18n";
import cloudscapeMessages from "@cloudscape-design/components/i18n/messages/all.en";
import { Outlet, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    setSideNavOpen,
    selectPageTitle,
    selectSideNavOpen,
    selectInfoMessages,
    removeInfoMessage
} from "src/core/redux";
import { useAppDispatch } from "src/core/redux/hooks";
import { SentinelDefaultInfoSection } from "./help/default-info-section";
import { SentinelOnboardingPageInfoSection } from "./help/onboarding-page-info-section";
import { SentinelDryRunInfoSection } from "./help/dry-run-info-section";
import { SentinelLeadershipDashboardInfoSection } from "./help/leadership-dashboard-info-section";
import { CREATE_ISSUE_URL, LEADERSHIP_NAV_TITLE, LEADERSHIP_PAGE_TITLE } from "src/web/constants";
const LOCALE = "en";
const menuItems = [
    {
        type: "link",
        text: "Overview",
        href: "/#/",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: "Onboard Pipelines",
        href: "/#/onboard",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: "Dry Run Mode",
        href: "/#/dry-run",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: "Teams",
        href: "/#/team",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: LEADERSHIP_NAV_TITLE,
        href: "/#/leadership-dashboard",
    } as SideNavigationProps.Item,
];
const sideNavMenuItems = menuItems;
sideNavMenuItems.push({
    type: "link",
    text: "Create Issue",
    href: CREATE_ISSUE_URL,
    external: true,
} as SideNavigationProps.Item);

const infoSectionForPage = (pageTitle: string): React.ReactNode => {
    switch (pageTitle) {
    case "Onboarding Page":
        return <SentinelOnboardingPageInfoSection />;
    case "Dry Run Mode":
        return <SentinelDryRunInfoSection />;
    case LEADERSHIP_PAGE_TITLE:
        return <SentinelLeadershipDashboardInfoSection />;
    default:
        return <SentinelDefaultInfoSection />;
    }
};

export const SentinelLayout = () => {
    const pageTitle = useSelector(selectPageTitle);
    const navOpen = useSelector(selectSideNavOpen);
    const infoMessages = useSelector(selectInfoMessages);
    const dispatch = useAppDispatch();
    const [messages, setMessages] = useState<typeof infoMessages>([]);

    const [toolsOpen, setToolsOpen] = useState(false);
    const [showPageInfo, setShowPageInfo] = useState(false);

    const openPageInfo = () => {
        setShowPageInfo(true);
        setToolsOpen(true);
    };

    useEffect(() => {
        const mappedMessages = infoMessages.map(message => ({
            ...message,
            onDismiss: () => dispatch(removeInfoMessage(message))
        }));
        setMessages(mappedMessages);
    }, [infoMessages]);

    return (
        <I18nProvider locale={LOCALE} messages={[cloudscapeMessages]}>
            <AppLayout
                navigationOpen={navOpen}
                navigation={
                    <SideNavigation
                        header={{
                            href: "#",
                            text: "Dependency Sentinel",
                        }}
                        items={sideNavMenuItems}
                    />
                }
                notifications={
                    <Flashbar
                        items={messages}
                    />
                }
                onNavigationChange={(e) => dispatch(setSideNavOpen(e.detail.open))}
                onToolsChange={({ detail }) => {
                    setShowPageInfo(false);
                    setToolsOpen(detail.open);
                }}
                toolsOpen={toolsOpen}
                tools={
                    showPageInfo ? (
                        infoSectionForPage(pageTitle)
                    ) : (
                        <SentinelDefaultInfoSection />
                    )
                }
                content={
                    <ContentLayout
                        header={
                            <Header variant="h1" info={
                                <Link
                                    variant="info"
                                    onFollow={openPageInfo}
                                >
                                Info
                                </Link>}>
                                {pageTitle}
                            </Header>
                        }
                    >
                        <Outlet context={[toolsOpen, setToolsOpen]}/>
                    </ContentLayout>
                }
            />
        </I18nProvider>
    );
};
export default SentinelLayout;

export function useInfoSection() {
    return useOutletContext<
      [
        boolean, // Whether the info panel is open
        React.Dispatch<React.SetStateAction<boolean>> // Open or close the info panel
      ]
    >();
}